import { useEffect, useRef, useState } from 'react';
import { Upload, FileSpreadsheet, ArrowRight, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { useImportSubscribers, useAudiences, type CsvImportResult } from '@/hooks/useNewsletter';
import { isLikelyEmail } from '@/types/newsletter';

type Props = {
  open: boolean;
  onClose: () => void;
};

type ParseState =
  | { phase: 'upload' }
  | { phase: 'mapping'; headers: string[]; rows: Array<Record<string, string>>; fileName: string }
  | { phase: 'preview'; headers: string[]; rows: Array<Record<string, string>>; mapping: Record<string, string>; fileName: string }
  | { phase: 'result'; result: CsvImportResult };

const FIELD_OPTIONS = [
  { value: '',          label: '— Skip this column —' },
  { value: 'email',     label: 'Email Address *' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName',  label: 'Last Name' },
  { value: 'company',   label: 'Company' },
  { value: 'title',     label: 'Title' },
  { value: 'phone',     label: 'Phone' },
  { value: 'tags',      label: 'Tags (comma-separated)' },
];

/** Naive but solid CSV parser — handles quoted fields with embedded commas and newlines */
function parseCsv(text: string): { headers: string[]; rows: Array<Record<string, string>> } {
  const result: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"'; i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else {
      if (ch === '"' && cell === '') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(cell); cell = '';
      } else if (ch === '\n' || ch === '\r') {
        if (cell !== '' || row.length > 0) { row.push(cell); cell = ''; result.push(row); row = []; }
        if (ch === '\r' && text[i + 1] === '\n') i++;
      } else {
        cell += ch;
      }
    }
  }
  if (cell !== '' || row.length > 0) { row.push(cell); result.push(row); }

  if (result.length === 0) return { headers: [], rows: [] };
  const headers = result[0].map((h) => h.trim());
  const rows = result.slice(1)
    .filter((r) => r.some((c) => c.trim()))
    .map((r) => Object.fromEntries(headers.map((h, i) => [h, (r[i] ?? '').trim()])));
  return { headers, rows };
}

function guessMapping(headers: string[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  for (const h of headers) {
    const low = h.toLowerCase().replace(/[\s_-]/g, '');
    if (low.includes('email')) mapping[h] = 'email';
    else if (low.includes('first') && low.includes('name')) mapping[h] = 'firstName';
    else if (low.includes('last') && low.includes('name')) mapping[h] = 'lastName';
    else if (low === 'name' && !Object.values(mapping).includes('firstName')) mapping[h] = 'firstName';
    else if (low.includes('company') || low.includes('organization') || low === 'employer') mapping[h] = 'company';
    else if (low.includes('title') || low === 'position' || low === 'role') mapping[h] = 'title';
    else if (low.includes('phone') || low === 'tel' || low === 'mobile') mapping[h] = 'phone';
    else if (low === 'tag' || low === 'tags' || low === 'group') mapping[h] = 'tags';
    else mapping[h] = '';
  }
  return mapping;
}

export function CsvImportModal({ open, onClose }: Props) {
  const { data: audiences } = useAudiences();
  const importMut = useImportSubscribers();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<ParseState>({ phase: 'upload' });
  const [audienceId, setAudienceId] = useState<string>('');
  const [extraTags, setExtraTags] = useState<string>('');

  useEffect(() => {
    if (open) {
      setState({ phase: 'upload' });
      setAudienceId('');
      setExtraTags('');
    }
  }, [open]);

  async function handleFile(file: File) {
    try {
      const text = await file.text();
      const { headers, rows } = parseCsv(text);
      if (headers.length === 0 || rows.length === 0) {
        toast.error('Could not parse the file. Make sure it has a header row.');
        return;
      }
      setState({ phase: 'mapping', headers, rows, fileName: file.name });
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Failed to read file.');
    }
  }

  function updateMapping(header: string, field: string) {
    if (state.phase !== 'mapping') return;
    const next = { ...guessMapping(state.headers), ...(state as { mapping?: Record<string, string> }).mapping, [header]: field };
    // Enforce single mapping to 'email'
    if (field === 'email') {
      for (const h of Object.keys(next)) {
        if (h !== header && next[h] === 'email') next[h] = '';
      }
    }
    setState({ ...state, mapping: next } as ParseState);
  }

  function toPreview() {
    if (state.phase !== 'mapping') return;
    const mapping = (state as { mapping?: Record<string, string> }).mapping ?? guessMapping(state.headers);
    const emailCol = Object.entries(mapping).find(([, v]) => v === 'email')?.[0];
    if (!emailCol) {
      toast.error('Please map a column to "Email Address".');
      return;
    }
    setState({ phase: 'preview', headers: state.headers, rows: state.rows, mapping, fileName: state.fileName });
  }

  async function runImport() {
    if (state.phase !== 'preview') return;
    try {
      const result = await importMut.mutateAsync({
        rows: state.rows,
        mapping: state.mapping,
        audienceIds: audienceId ? [audienceId] : [],
        tags: extraTags ? extraTags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        source: `csv:${state.fileName}`,
      });
      setState({ phase: 'result', result });
      toast.success(`Imported ${result.imported}${result.updated ? `, updated ${result.updated}` : ''}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Import failed.');
    }
  }

  const mapping = state.phase === 'mapping' || state.phase === 'preview'
    ? ((state as { mapping?: Record<string, string> }).mapping ?? (state.phase === 'mapping' ? guessMapping(state.headers) : {}))
    : {};
  const emailColumn = Object.entries(mapping).find(([, v]) => v === 'email')?.[0];

  // Preview stats
  const validEmailCount = state.phase === 'preview'
    ? state.rows.filter((r) => emailColumn && isLikelyEmail(r[emailColumn] ?? '')).length
    : 0;
  const invalidEmailCount = state.phase === 'preview'
    ? state.rows.length - validEmailCount
    : 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker="Import Subscribers"
      title="Upload a CSV of your customers."
      description="Import up to 10,000 rows. Existing subscribers (matched by email) are updated; new ones are added."
      size="lg"
      footer={
        <>
          {state.phase === 'upload' && (
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
          )}
          {state.phase === 'mapping' && (
            <>
              <Button variant="ghost" onClick={() => setState({ phase: 'upload' })}>Back</Button>
              <Button onClick={toPreview} rightIcon={<ArrowRight size={13} />}>
                Review Import
              </Button>
            </>
          )}
          {state.phase === 'preview' && (
            <>
              <Button variant="ghost" onClick={() => setState({ phase: 'mapping', headers: state.headers, rows: state.rows, fileName: state.fileName })}>
                Back
              </Button>
              <Button onClick={runImport} loading={importMut.isPending} leftIcon={<Upload size={13} />}>
                Import {validEmailCount} Subscriber{validEmailCount === 1 ? '' : 's'}
              </Button>
            </>
          )}
          {state.phase === 'result' && (
            <Button onClick={onClose}>Done</Button>
          )}
        </>
      }
    >
      {state.phase === 'upload' && (
        <UploadStep
          onFile={handleFile}
          onPick={() => fileInputRef.current?.click()}
          fileInputRef={fileInputRef}
        />
      )}

      {state.phase === 'mapping' && (
        <div className="py-2 space-y-5">
          <div className="flex items-center gap-3 p-3 bg-bg-subtle border border-border rounded-sm">
            <FileSpreadsheet size={16} className="text-brass-300" />
            <div className="text-sm text-text flex-1">
              <span className="font-medium">{state.fileName}</span>
              <span className="text-text-dim ml-2">· {state.rows.length} rows · {state.headers.length} columns</span>
            </div>
          </div>

          <div>
            <div className="kicker mb-2">Column Mapping</div>
            <p className="text-xs text-text-muted mb-3">
              Tell us which column contains which field. We've guessed — confirm or adjust below.
            </p>

            <div className="border border-border rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-bg-subtle">
                    <th className="kicker px-4 py-2 font-normal text-left">CSV Column</th>
                    <th className="kicker px-4 py-2 font-normal text-left">Sample Value</th>
                    <th className="kicker px-4 py-2 font-normal text-left w-56">Maps To</th>
                  </tr>
                </thead>
                <tbody>
                  {state.headers.map((h) => (
                    <tr key={h} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 font-medium text-text">{h}</td>
                      <td className="px-4 py-2 text-2xs text-text-dim font-mono truncate max-w-xs">
                        {state.rows[0]?.[h] || <span className="italic">empty</span>}
                      </td>
                      <td className="px-4 py-2">
                        <Select
                          value={mapping[h] ?? ''}
                          onChange={(e) => updateMapping(h, e.target.value)}
                          options={FIELD_OPTIONS}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {state.phase === 'preview' && (
        <div className="py-2 space-y-5">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-px bg-border rounded-sm overflow-hidden border border-border">
            <PreviewStat label="Total Rows"     value={state.rows.length} />
            <PreviewStat label="Valid Emails"   value={validEmailCount} accent />
            <PreviewStat label="Invalid"        value={invalidEmailCount} alert={invalidEmailCount > 0} />
          </div>

          {/* Audience + tags */}
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Add to Audience"
              value={audienceId}
              onChange={(e) => setAudienceId(e.target.value)}
              options={[
                { value: '', label: '— None —' },
                ...(audiences ?? []).filter((a) => a.kind === 'static').map((a) => ({
                  value: a.id, label: a.name,
                })),
              ]}
              hint="Optional. Dynamic audiences auto-evaluate."
            />
            <Input
              label="Tags"
              placeholder="imported-2026-04, vip-customer"
              value={extraTags}
              onChange={(e) => setExtraTags(e.target.value)}
              hint="Comma-separated. Applied to every imported row."
            />
          </div>

          {/* Preview rows */}
          <div>
            <div className="kicker mb-2">First 10 rows preview</div>
            <div className="border border-border rounded-sm overflow-hidden overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-bg-subtle border-b border-border">
                    <th className="kicker px-3 py-2 font-normal text-left">Email</th>
                    <th className="kicker px-3 py-2 font-normal text-left">First</th>
                    <th className="kicker px-3 py-2 font-normal text-left">Last</th>
                    <th className="kicker px-3 py-2 font-normal text-left">Company</th>
                    <th className="kicker px-3 py-2 font-normal w-12" />
                  </tr>
                </thead>
                <tbody>
                  {state.rows.slice(0, 10).map((row, i) => {
                    const email = emailColumn ? row[emailColumn] : '';
                    const firstCol = Object.entries(state.mapping).find(([, v]) => v === 'firstName')?.[0];
                    const lastCol  = Object.entries(state.mapping).find(([, v]) => v === 'lastName')?.[0];
                    const compCol  = Object.entries(state.mapping).find(([, v]) => v === 'company')?.[0];
                    const valid = isLikelyEmail(email);
                    return (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className={`px-3 py-2 font-mono ${valid ? 'text-text' : 'text-danger'}`}>
                          {email || <span className="italic">empty</span>}
                        </td>
                        <td className="px-3 py-2 text-text-muted">{firstCol ? row[firstCol] : '—'}</td>
                        <td className="px-3 py-2 text-text-muted">{lastCol ? row[lastCol] : '—'}</td>
                        <td className="px-3 py-2 text-text-muted">{compCol ? row[compCol] : '—'}</td>
                        <td className="px-3 py-2">
                          {valid
                            ? <CheckCircle2 size={12} className="text-success" />
                            : <AlertCircle size={12} className="text-danger" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {state.phase === 'result' && (
        <div className="py-6 space-y-4">
          <div className="text-center">
            <div className="h-16 w-16 mx-auto rounded-full bg-success/10 border border-success/40 flex items-center justify-center mb-4">
              <CheckCircle2 size={28} className="text-success" />
            </div>
            <h3 className="font-display text-2xl text-text mb-1">Import complete</h3>
            <p className="text-sm text-text-muted">
              {state.result.imported} new · {state.result.updated} updated · {state.result.skipped} skipped
            </p>
          </div>

          <div className="grid grid-cols-2 gap-px bg-border rounded-sm overflow-hidden border border-border">
            <PreviewStat label="Imported"   value={state.result.imported} accent />
            <PreviewStat label="Updated"    value={state.result.updated} />
            <PreviewStat label="Skipped"    value={state.result.skipped} />
            <PreviewStat label="Invalid"    value={state.result.invalid} alert={state.result.invalid > 0} />
            <PreviewStat label="Suppressed" value={state.result.suppressed} />
          </div>

          {state.result.errors.length > 0 && (
            <div>
              <div className="kicker mb-2">Errors · {state.result.errors.length}</div>
              <div className="max-h-40 overflow-y-auto border border-border rounded-sm text-xs">
                {state.result.errors.slice(0, 20).map((e, i) => (
                  <div key={i} className="px-3 py-2 border-b border-border last:border-0 flex items-center gap-2">
                    <X size={10} className="text-danger" />
                    <span className="text-text-dim">Row {e.row}:</span>
                    <span className="text-text">{e.email ?? ''}</span>
                    <span className="text-danger ml-auto">{e.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function UploadStep({
  onFile, onPick, fileInputRef,
}: {
  onFile: (f: File) => void;
  onPick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}) {
  const [isDragging, setIsDragging] = useState(false);
  return (
    <div className="py-4">
      <div
        onClick={onPick}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault(); setIsDragging(false);
          const file = e.dataTransfer.files[0];
          if (file) onFile(file);
        }}
        className={`
          cursor-pointer border-2 border-dashed rounded-sm p-12 text-center transition-colors
          ${isDragging
            ? 'border-brass-400 bg-brass-400/5'
            : 'border-border hover:border-border-strong hover:bg-bg-hover'}
        `}
      >
        <Upload size={32} className="mx-auto text-text-dim mb-3" />
        <div className="font-display text-lg text-text mb-1">Drop CSV here</div>
        <div className="text-xs text-text-muted">or click to browse</div>
        <div className="text-2xs text-text-dim mt-4">
          First row should be column headers. Must include at least an email column.
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
        className="hidden"
      />
    </div>
  );
}

function PreviewStat({ label, value, accent, alert }: { label: string; value: number; accent?: boolean; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-3">
      <div className="kicker mb-1">{label}</div>
      <div className={`font-display text-xl tabular ${accent ? 'text-brass-300' : alert ? 'text-danger' : 'text-text'}`}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}
